import type { FileRouteTypes } from "~/routeTree.gen";

/** A path the router can navigate to, taken from the generated route tree. */
export type RoutePath = FileRouteTypes["to"];

type PathParamName<TPath extends string> =
  TPath extends `${string}$${infer TParam}/${infer TRest}`
    ? TParam | PathParamName<TRest>
    : TPath extends `${string}$${infer TParam}`
      ? TParam
      : never;

type PathParams<TPath extends RoutePath> = Record<PathParamName<TPath>, string>;

type BuiltPath<TPath extends RoutePath> =
  PathParamName<TPath> extends never ? TPath : string;

/**
 * Builds the URL of a route. Paths and their params are checked against the
 * route tree, so a renamed or removed route fails type-checking here.
 */
export const routePath = <TPath extends RoutePath>(
  path: TPath,
  ...[params]: PathParamName<TPath> extends never ? [] : [PathParams<TPath>]
) =>
  path.replace(/\$(\w+)/g, (_, name: PathParamName<TPath>) =>
    encodeURIComponent((params as PathParams<TPath>)[name]),
  ) as BuiltPath<TPath>;
